import type {
  CategoryDto,
  CreateOrderDto,
  OrderDto,
  AuthResult,
  AddPaymentDto,
  PaymentMethod,
  RestaurantInfo,
  LoyaltyMember,
  LoyaltyLookup,
  LoyaltyConfigDto,
  LoyaltyMemberRow,
  PromoConfigDto,
  PartnerRow,
  CashSessionRow,
  ZClosureRow,
  ZPreview,
  AuditRow,
  StaffRow,
  PermissionSet,
  StaffRole,
  MessagingCustomer,
  MessagingSendResult,
  CampaignRow,
  DeliveryConfig,
  DeliveryQuoteResult,
  CustomerPrefill,
  ReportSummary,
  RecentOrder,
  StationPrinter,
  ShoppingItem,
  ReservationRow,
  CreateReservationDto,
  CardReaderRow,
  TerminalAvailability,
  TerminalPaymentDto,
} from '@taj/types';

// Même origine que la page (l'API est relayée par Vite/serveur sur /api).
// → fonctionne en local, sur le wifi ET à distance via un tunnel (un seul lien).
const API = import.meta.env.VITE_API_URL ?? '';

function authHeader(): Record<string, string> {
  const t = localStorage.getItem('taj_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}/api${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init?.headers ?? {}) },
  });
  // Session expirée (token invalide) → on nettoie et on renvoie à l'écran de connexion.
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    localStorage.removeItem('taj_token');
    localStorage.removeItem('taj_auth');
    location.reload();
    throw new Error('Session expirée — reconnectez-vous.');
  }
  if (!res.ok) {
    const msg = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(Array.isArray(msg.message) ? msg.message.join(', ') : msg.message);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export const api = {
  getMenu: () => req<CategoryDto[]>('/catalog/menu'),
  createOrder: (dto: CreateOrderDto) =>
    req<OrderDto>('/orders', { method: 'POST', body: JSON.stringify(dto) }),
  activeOrders: () => req<OrderDto[]>('/orders/active'),
  getOrder: (id: string) => req<OrderDto>(`/orders/${id}`),
  getOrderByNumber: (number: number) => req<OrderDto>(`/orders/number/${number}`),
  closedOrders: () => req<OrderDto[]>('/orders/closed'),
  reopenOrder: (id: string) => req<OrderDto>(`/orders/${id}/reopen`, { method: 'POST' }),
  completeOrder: (id: string) => req<OrderDto>(`/orders/${id}/complete`, { method: 'POST' }),
  sendToKitchen: (id: string) => req<OrderDto>(`/orders/${id}/send-to-kitchen`, { method: 'POST' }),
  updateOrderLines: (id: string, lines: { productId: string; qty: number; optionIds?: string[] }[]) =>
    req<OrderDto>(`/orders/${id}/lines`, { method: 'PUT', body: JSON.stringify({ lines }) }),
  advanceOrder: (id: string) => req<OrderDto>(`/orders/${id}/advance`, { method: 'POST' }),
  applyDiscount: (id: string, body: { type: 'PERCENT' | 'AMOUNT'; value: number; reason?: string }) =>
    req<OrderDto>(`/orders/${id}/discount`, { method: 'POST', body: JSON.stringify(body) }),
  cancelOrder: (id: string, reason?: string) =>
    req<OrderDto>(`/orders/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  listReservations: (from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const s = q.toString();
    return req<ReservationRow[]>(`/reservations${s ? `?${s}` : ''}`);
  },
  createReservation: (dto: CreateReservationDto) =>
    req<ReservationRow>('/reservations/staff', { method: 'POST', body: JSON.stringify(dto) }),
  confirmReservation: (id: string) => req<ReservationRow>(`/reservations/${id}/confirm`, { method: 'POST' }),
  declineReservation: (id: string, reason?: string) =>
    req<ReservationRow>(`/reservations/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),
  addPayment: (dto: AddPaymentDto) =>
    req<{ fullyPaid: boolean; remainingCents: number; paidCents: number; order: OrderDto | null }>('/payments', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
  listPayments: (orderId: string) =>
    req<{ id: string; method: PaymentMethod; amountCents: number; status: string; createdAt: string }[]>(`/payments/order/${orderId}`),
  voidPayment: (paymentId: string) =>
    req<{ ok: boolean; order: OrderDto | null }>(`/payments/${paymentId}/void`, { method: 'POST' }),
  loginPin: (pin: string) =>
    req<AuthResult>('/auth/login/pin', { method: 'POST', body: JSON.stringify({ pin }) }),
  loginAdmin: (fullName: string, password: string) =>
    req<AuthResult>('/auth/login/admin', {
      method: 'POST',
      body: JSON.stringify({ fullName, password }),
    }),
  loginQr: (token: string) =>
    req<AuthResult>('/auth/login/qr', { method: 'POST', body: JSON.stringify({ token }) }),
  reportSummary: (opts: { period?: 'day' | 'week'; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (opts.period) q.set('period', opts.period);
    if (opts.from) q.set('from', opts.from);
    if (opts.to) q.set('to', opts.to);
    return req<ReportSummary>(`/reports/summary?${q.toString()}`);
  },
  reportRecent: () => req<RecentOrder[]>('/reports/recent'),
  listPrinters: () => req<StationPrinter[]>('/printing/stations'),
  addStation: (name: string) =>
    req<StationPrinter[]>('/printing/stations', { method: 'POST', body: JSON.stringify({ name }) }),
  deleteStation: (stationId: string) =>
    req<StationPrinter[]>(`/printing/station/${stationId}`, { method: 'DELETE' }),
  setPrinter: (stationId: string, dto: { ip: string; port?: number; active?: boolean }) =>
    req<StationPrinter[]>(`/printing/station/${stationId}`, { method: 'PUT', body: JSON.stringify(dto) }),
  testPrinter: (stationId: string) => req<{ ok: boolean }>(`/printing/test/${stationId}`, { method: 'POST' }),

  // ── Terminaux de paiement (SumUp Solo) ──
  listCardReaders: () => req<CardReaderRow[]>('/payments/terminal/readers'),
  pairCardReader: (station: string, pairingCode: string, name?: string) =>
    req<CardReaderRow>('/payments/terminal/readers', { method: 'POST', body: JSON.stringify({ station, pairingCode, name }) }),
  setCardReaderActive: (station: string, active: boolean) =>
    req<CardReaderRow>(`/payments/terminal/readers/${encodeURIComponent(station)}/active`, { method: 'PUT', body: JSON.stringify({ active }) }),
  unpairCardReader: (station: string) =>
    req<{ ok: true }>(`/payments/terminal/readers/${encodeURIComponent(station)}`, { method: 'DELETE' }),
  /** Ce poste dispose-t-il d'un terminal utilisable ? */
  terminalAvailability: (station?: string) =>
    req<TerminalAvailability>(`/payments/terminal/availability${station ? `?station=${encodeURIComponent(station)}` : ''}`),
  /** Envoie le montant sur le terminal du poste (le client paie sur le lecteur). */
  startTerminalPayment: (orderId: string, station: string, amountCents?: number) =>
    req<TerminalPaymentDto>('/payments/terminal/start', { method: 'POST', body: JSON.stringify({ orderId, station, amountCents }) }),
  terminalPaymentStatus: (id: string) => req<TerminalPaymentDto>(`/payments/terminal/status/${id}`),
  cancelTerminalPayment: (id: string) => req<TerminalPaymentDto>(`/payments/terminal/cancel/${id}`, { method: 'POST' }),
  printReceipt: (orderId: string, station?: string, opts?: { detail?: boolean; covers?: number; label?: string }) => {
    const q = new URLSearchParams();
    if (station) q.set('station', station);
    if (opts?.detail === false) q.set('detail', 'false');
    if (opts?.covers && opts.covers > 1) q.set('covers', String(opts.covers));
    if (opts?.label?.trim()) q.set('label', opts.label.trim());
    const qs = q.toString();
    return req<{ ok: boolean; printer: string }>(`/printing/receipt/${orderId}${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  emailReceipt: (orderId: string, email: string) =>
    req<{ ok: boolean; email: string }>(`/printing/receipt-email/${orderId}`, { method: 'POST', body: JSON.stringify({ email }) }),
  printLabels: (orderId: string, station?: string) => {
    const q = station ? `?station=${encodeURIComponent(station)}` : '';
    return req<{ ok: boolean; printer: string; count: number }>(`/printing/labels/${orderId}${q}`, { method: 'POST' });
  },
  joinLoyalty: (dto: { phone: string; name?: string; email?: string; orderId?: string }) =>
    req<LoyaltyMember>('/loyalty/join', { method: 'POST', body: JSON.stringify(dto) }),
  lookupLoyalty: (phoneOrCode: string) =>
    req<LoyaltyLookup>('/loyalty/lookup', { method: 'POST', body: JSON.stringify({ phoneOrCode }) }),
  // ── Livraison ──
  getDeliveryConfig: () => req<DeliveryConfig>('/delivery/config'),
  updateDeliveryConfig: (dto: Partial<DeliveryConfig>) =>
    req<DeliveryConfig>('/delivery/config', { method: 'PUT', body: JSON.stringify(dto) }),
  deliveryQuote: (lat: number, lon: number) =>
    req<DeliveryQuoteResult>('/delivery/quote', { method: 'POST', body: JSON.stringify({ lat, lon }) }),
  deliveryCustomer: (phone: string) =>
    req<CustomerPrefill | null>(`/delivery/customer?phone=${encodeURIComponent(phone)}`),
  searchCustomers: (q: string) =>
    req<CustomerPrefill[]>(`/delivery/customers?q=${encodeURIComponent(q)}`),
  // ── Liste de courses ──
  listShopping: () => req<ShoppingItem[]>('/shopping'),
  addShopping: (item: { name: string; category?: string; unit?: string; quantity?: string }) =>
    req<ShoppingItem>('/shopping', { method: 'POST', body: JSON.stringify(item) }),
  updateShopping: (id: string, patch: Partial<Pick<ShoppingItem, 'name' | 'category' | 'unit' | 'quantity' | 'checked'>>) =>
    req<ShoppingItem>(`/shopping/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteShopping: (id: string) => req<{ ok: boolean }>(`/shopping/${id}`, { method: 'DELETE' }),
  clearShoppingChecks: () => req<{ ok: boolean }>('/shopping/clear', { method: 'PUT' }),
  printShopping: () => req<{ ok: boolean }>('/printing/shopping', { method: 'POST' }),
  // ── Fidélité ──
  getLoyaltyConfig: () => req<LoyaltyConfigDto>('/loyalty/config'),
  updateLoyaltyConfig: (dto: Partial<LoyaltyConfigDto>) =>
    req<LoyaltyConfigDto>('/loyalty/config', { method: 'PUT', body: JSON.stringify(dto) }),
  loyaltyMembers: (q?: string) =>
    req<LoyaltyMemberRow[]>(`/loyalty/members${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  // ── Promotions ──
  getPromoConfig: () => req<PromoConfigDto>('/promotions/config'),
  updatePromoConfig: (dto: Partial<PromoConfigDto>) =>
    req<PromoConfigDto>('/promotions/config', { method: 'PUT', body: JSON.stringify(dto) }),
  // ── Partenaires ──
  listPartners: () => req<PartnerRow[]>('/partners'),
  createPartner: (p: { type: string; commissionPct?: number; active?: boolean }) =>
    req<PartnerRow>('/partners', { method: 'POST', body: JSON.stringify(p) }),
  updatePartner: (id: string, p: Partial<Pick<PartnerRow, 'type' | 'commissionPct' | 'active'>>) =>
    req<PartnerRow>(`/partners/${id}`, { method: 'PUT', body: JSON.stringify(p) }),
  deletePartner: (id: string) => req<{ ok: boolean }>(`/partners/${id}`, { method: 'DELETE' }),
  // ── Clôture (Ticket Z) ──
  cloturePreview: () => req<ZPreview>('/cloture/preview'),
  clotureSession: () => req<CashSessionRow | null>('/cloture/session'),
  clotureOpen: (creds: { pin?: string; fullName?: string; password?: string; openingFloatCents?: number }) =>
    req<CashSessionRow>('/cloture/open', { method: 'POST', body: JSON.stringify(creds) }),
  clotureClose: () => req<ZClosureRow>('/cloture/close', { method: 'POST' }),
  clotureList: () => req<ZClosureRow[]>('/cloture/list'),
  cloturePrint: (id: string) => req<{ ok: boolean; printer?: string; reason?: string }>(`/cloture/${id}/print`, { method: 'POST' }),
  // ── Audit ──
  listAudit: (action?: string) => req<AuditRow[]>(`/audit${action ? `?action=${encodeURIComponent(action)}` : ''}`),
  // ── Comptes & droits ──
  listStaff: () => req<StaffRow[]>('/staff'),
  createStaff: (s: { fullName: string; role: StaffRole; pin?: string; password?: string; permissions?: Partial<PermissionSet> }) =>
    req<StaffRow>('/staff', { method: 'POST', body: JSON.stringify(s) }),
  updateStaff: (id: string, s: { fullName?: string; role?: StaffRole; active?: boolean; pin?: string; password?: string; permissions?: Partial<PermissionSet> }) =>
    req<StaffRow>(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(s) }),
  deleteStaff: (id: string) => req<{ ok: boolean }>(`/staff/${id}`, { method: 'DELETE' }),
  regenStaffQr: (id: string) => req<StaffRow>(`/staff/${id}/regen-qr`, { method: 'POST' }),
  // ── Messages clients (SMS) ──
  messagingAudience: () => req<MessagingCustomer[]>('/messaging/audience'),
  sendMessages: (dto: { message: string; subject?: string; phones?: string[]; emails?: string[]; photo?: string }) =>
    req<MessagingSendResult>('/messaging/send', { method: 'POST', body: JSON.stringify(dto) }),
  addContact: (c: { name?: string; phone?: string; email?: string }) =>
    req<MessagingCustomer>('/messaging/contact', { method: 'POST', body: JSON.stringify(c) }),
  deleteContact: (id: string) => req<{ ok: boolean }>(`/messaging/contact/${id}`, { method: 'DELETE' }),
  importContacts: (contacts: { name?: string; phone?: string; email?: string }[]) =>
    req<{ added: number; skipped: number }>('/messaging/import', { method: 'POST', body: JSON.stringify({ contacts }) }),
  listCampaigns: () => req<CampaignRow[]>('/messaging/campaigns'),
  createCampaign: (c: { name?: string; message: string; subject?: string; channel?: string; scheduledAt: string; recurrence?: string }) =>
    req<CampaignRow>('/messaging/campaigns', { method: 'POST', body: JSON.stringify(c) }),
  deleteCampaign: (id: string) => req<{ ok: boolean }>(`/messaging/campaigns/${id}`, { method: 'DELETE' }),
  receiveShopping: (id: string) => req<{ ok: boolean; added: number; newQty: number | null; name: string }>(`/shopping/${id}/receive`, { method: 'POST' }),
  receiveCheckedShopping: () => req<{ ok: boolean; count: number; portions: number }>('/shopping/receive-checked', { method: 'POST' }),
  getSettings: () => req<RestaurantInfo>('/settings'),
  updateSettings: (info: RestaurantInfo) =>
    req<RestaurantInfo>('/settings', { method: 'PUT', body: JSON.stringify(info) }),

  // ── Gestion de la carte (back-office) ──
  listCategories: () =>
    req<{ id: string; name: string; nameEn: string | null; emoji: string | null; imageUrl: string | null; position: number; stationId: string | null }[]>(
      '/catalog/categories',
    ),
  importMenu: (rows: MenuImportRow[], replace: boolean) =>
    req<{ categories: number; created: number; updated: number; hidden: number }>(
      '/catalog/import',
      { method: 'POST', body: JSON.stringify({ rows, replace }) },
    ),
  createProduct: (p: ProductInput) =>
    req<{ id: string }>('/catalog/products', { method: 'POST', body: JSON.stringify(p) }),
  updateProduct: (id: string, p: Partial<ProductInput>) =>
    req<{ id: string }>(`/catalog/products/${id}`, { method: 'PUT', body: JSON.stringify(p) }),
  deleteProduct: (id: string) =>
    req<{ ok: boolean }>(`/catalog/products/${id}`, { method: 'DELETE' }),
  createCategory: (c: { name: string; nameEn?: string; emoji?: string; imageUrl?: string }) =>
    req<{ id: string }>('/catalog/categories', { method: 'POST', body: JSON.stringify(c) }),
  updateCategory: (id: string, c: { name?: string; nameEn?: string; emoji?: string; imageUrl?: string | null }) =>
    req<{ id: string }>(`/catalog/categories/${id}`, { method: 'PUT', body: JSON.stringify(c) }),
  deleteCategory: (id: string) =>
    req<{ ok: boolean }>(`/catalog/categories/${id}`, { method: 'DELETE' }),
  // Groupes d'options (Suppléments, Sauces…) + options
  createOptionGroup: (g: { productId: string; name: string; minSelect?: number; maxSelect?: number }) =>
    req<{ id: string }>('/catalog/option-groups', { method: 'POST', body: JSON.stringify(g) }),
  updateOptionGroup: (id: string, g: { name?: string; minSelect?: number; maxSelect?: number }) =>
    req<{ id: string }>(`/catalog/option-groups/${id}`, { method: 'PUT', body: JSON.stringify(g) }),
  deleteOptionGroup: (id: string) =>
    req<{ ok: boolean }>(`/catalog/option-groups/${id}`, { method: 'DELETE' }),
  createOption: (o: { groupId: string; name: string; priceDelta?: number; allergens?: string[]; imageUrl?: string }) =>
    req<{ id: string }>('/catalog/options', { method: 'POST', body: JSON.stringify(o) }),
  updateOption: (id: string, o: { name?: string; priceDelta?: number; allergens?: string[]; imageUrl?: string | null }) =>
    req<{ id: string }>(`/catalog/options/${id}`, { method: 'PUT', body: JSON.stringify(o) }),
  deleteOption: (id: string) =>
    req<{ ok: boolean }>(`/catalog/options/${id}`, { method: 'DELETE' }),
};

export interface AddressSuggestion {
  label: string;
  lat: number;
  lon: number;
}

/** Autocomplétion d'adresse via la Base Adresse Nationale (data.gouv.fr — gratuit, sans clé). */
export async function searchAddress(query: string): Promise<AddressSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  try {
    const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.features ?? []).map((f: any) => ({
      label: f.properties.label as string,
      lat: f.geometry.coordinates[1] as number,
      lon: f.geometry.coordinates[0] as number,
    }));
  } catch {
    return [];
  }
}

export interface MenuImportRow {
  category: string;
  name: string;
  priceCents: number;
  nameEn?: string;
  description?: string;
  descriptionEn?: string;
  allergens?: string[];
  badges?: string[];
  emoji?: string;
  station?: string;
  supplements?: { name: string; priceCents: number }[];
  sauces?: { name: string; priceCents: number }[];
}

export interface ProductInput {
  name: string;
  nameEn?: string;
  description?: string;
  descriptionEn?: string;
  categoryId: string;
  priceCents: number;
  emoji?: string;
  photoUrl?: string;
  available?: boolean;
  badges?: string[];
  allergens?: string[];
  trackStock?: boolean;
  stockQty?: number;
  threshold?: number;
  reorderQty?: string;
  reorderUnit?: string;
  portionsPerBatch?: number;
}
